import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/PasswordInput";
import { useAuth } from "@/hooks/auth/useAuth";
import { useMutation } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { Turnstile } from "@marsidev/react-turnstile";
import { useState } from "react";

interface SignUpFormData {
  email: string;
  password: string;
  full_name: string;
}

interface SignUpFormProps {
  onSuccess?: () => void;
}

export function SignUpForm({ onSuccess }: SignUpFormProps) {
  const { signUp } = useAuth();
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<SignUpFormData>();

  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);

  const signUpMutation = useMutation({
    mutationFn: async (data: SignUpFormData) =>
      signUp({
        email: data.email,
        password: data.password,
        full_name: data.full_name,
        options: { captchaToken: turnstileToken },
      }),
    onSuccess: (result) => {
      if (result.error) {
        if (result.error.code === "captcha_failed") {
          toast.error("Échec de la vérification CAPTCHA", {
            description: "Veuillez réessayer.",
          });
          setTurnstileToken(null);
        } else {
          toast.error("Échec de l'inscription");
        }
        return;
      }
      toast.success("Compte créé", {
        // description: "Vérifiez votre email pour confirmer votre compte.",
        description: "Bienvenue !",
      });
      reset();
      if (onSuccess) onSuccess();
    },
    onError: () => {
      toast.error("Une erreur inattendue s'est produite.");
    },
  });

  const loading = signUpMutation.isPending;

  const onSubmit = (data: SignUpFormData) => {
    signUpMutation.mutate(data);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="mt-6 space-y-4">
      <div className="space-y-2">
        <Label htmlFor="full_name">Nom</Label>
        <Input
          id="full_name"
          type="text"
          placeholder="Les personnages voient ce nom"
          {...register("full_name", {
            required: "Nom requis",
            minLength: {
              value: 2,
              message: "Le nom doit contenir au moins 2 caractères",
            },
          })}
          className={errors.full_name ? "border-red-500" : ""}
        />
        {errors.full_name && (
          <p className="text-sm text-red-500">{errors.full_name.message}</p>
        )}
      </div>
      <div className="space-y-2">
        <Label htmlFor="email">Email</Label>
        <Input
          id="email"
          type="email"
          placeholder="votre@email.com"
          {...register("email", {
            required: "Email requis",
            pattern: {
              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
              message: "Email invalide",
            },
          })}
          className={errors.email ? "border-red-500" : ""}
        />
        {errors.email && (
          <p className="text-sm text-red-500">{errors.email.message}</p>
        )}
      </div>
      <div className="space-y-2">
        <Label htmlFor="password">Mot de passe</Label>
        <PasswordInput
          id="password"
          placeholder="••••••••"
          error={!!errors.password}
          {...register("password", {
            required: "Mot de passe requis",
            minLength: {
              value: 6,
              message: "Le mot de passe doit contenir au moins 6 caractères",
            },
          })}
        />
        {errors.password && (
          <p className="text-sm text-red-500">{errors.password.message}</p>
        )}
      </div>

      <div className="flex w-full justify-center">
        <Turnstile
          siteKey={import.meta.env.VITE_TURNSTILE_SITE_KEY}
          onSuccess={setTurnstileToken}
          onExpire={() => setTurnstileToken(null)}
          onError={() => setTurnstileToken(null)}
          options={{
            theme: "light",
          }}
        />
      </div>

      <Button
        type="submit"
        className="w-full"
        disabled={loading || !turnstileToken}
      >
        {signUpMutation.isPending ?
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Inscription...
          </>
        : "S'inscrire"}
      </Button>
    </form>
  );
}
